import React, { useEffect, useRef, useState } from 'react';

/** IntersectionObserver hook. Fires once, then disconnects. */
export function useInView<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, {
      threshold
    });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return {
    ref,
    inView
  };
}

/** Masked line reveal: children slide up from behind an overflow-hidden mask. */
export function Reveal({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const {
    ref,
    inView
  } = useInView<HTMLSpanElement>(0.2);
  return <span ref={ref} className={`block overflow-hidden ${className}`} data-id="element-207">
      <span className="block will-change-transform" style={{
      transform: inView ? 'translateY(0)' : 'translateY(115%)',
      transition: `transform 1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
    }} data-id="element-208">
        {children}
      </span>
    </span>;
}

/**
 * Kinetic title: masked character-by-character reveal.
 * Splits into words (so wrapping stays clean), then characters.
 * Each character rises from behind an overflow-hidden mask with a stagger.
 */
export function CharReveal({
  text,
  className = '',
  baseDelay = 0,
  step = 34
}: {
  text: string;
  className?: string;
  baseDelay?: number;
  step?: number;
}) {
  const {
    ref,
    inView
  } = useInView<HTMLSpanElement>(0.25);
  let charIndex = 0;
  const words = text.split(' ');
  return <span ref={ref} className={`inline-block ${className}`} aria-label={text} role="text" data-id="element-209">
      {words.map((word, wi) => <span key={wi} className="inline-block whitespace-nowrap" aria-hidden="true" data-id="element-210">
          {word.split('').map(ch => {
        const i = charIndex++;
        return <span key={i} className="inline-block overflow-hidden align-bottom" data-id="element-211">
                <span className="inline-block will-change-transform" style={{
            transform: inView ? 'translateY(0)' : 'translateY(115%)',
            transition: `transform 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${baseDelay + i * step}ms`
          }} data-id="element-212">
                  {ch}
                </span>
              </span>;
      })}
          {wi < words.length - 1 && <span className="inline-block" data-id="element-213">{'\u00A0'}</span>}
        </span>)}
    </span>;
}

/** Fade-and-rise block for cards and grid items. */
export function FadeUp({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const {
    ref,
    inView
  } = useInView<HTMLDivElement>(0.15);
  return <div ref={ref} className={className} style={{
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(32px)',
    transition: `opacity 0.8s ease ${delay}ms, transform 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
  }} data-id="element-214">
      {children}
    </div>;
}

/** Igbo + English section label, the site's kicker convention. */
export function Kicker({
  ig,
  en,
  className = ''
}: {
  ig: string;
  en: string;
  className?: string;
}) {
  const {
    ref,
    inView
  } = useInView<HTMLParagraphElement>(0.3);
  return <p ref={ref} className={`font-monoui text-[11px] md:text-xs uppercase tracking-[0.22em] ${className}`} style={{
    opacity: inView ? 1 : 0,
    transition: 'opacity 0.9s ease 100ms'
  }} data-id="element-215">
      <span style={{
      color: 'var(--danfo)'
    }} data-id="element-216">✦</span>{' '}
      <span className="opacity-90" data-id="element-217">{ig}</span>{' '}
      <span className="opacity-40" data-id="element-218">/ {en}</span>
    </p>;
}